import { useState, useEffect } from "react";
import { Wallet as WalletIcon, ArrowDownToLine, Heart, Coins, Banknote, Mail } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";

export default function Wallet() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [cashing, setCashing] = useState(false);
  const [showCashout, setShowCashout] = useState(false);
  const [email, setEmail] = useState("");

  const load = () => api.get("/wallet").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => { if (user?.email) setEmail(user.email); }, [user]);

  const cashout = async () => {
    if (!email.trim()) { toast.error("Enter the email to receive your transfer."); return; }
    setCashing(true);
    try {
      const { data } = await api.post("/wallet/cashout", { email });
      toast.success(`$${data.cashed_out.toFixed(2)} ${data.status === "sent" ? "sent to" : "queued for transfer to"} ${data.email} 💛`);
      setShowCashout(false); load();
    }
    catch (e) { toast.error(apiErr(e)); }
    setCashing(false);
  };

  if (!data) return <div className="min-h-[60vh] flex items-center justify-center text-copper font-serif">Loading…</div>;

  const icon = { tip: Heart, community_split: Coins, cashout: Banknote };
  const labelType = { tip: "Direct tip", community_split: "Community jar share", cashout: "Cashout" };

  return (
    <div data-testid="online-wallet-page" className="max-w-2xl mx-auto px-4 py-8">
      <div className="flex flex-col items-center text-center">
        <p className="font-mono text-xs uppercase tracking-widest text-copper flex items-center gap-1"><WalletIcon className="w-3.5 h-3.5" /> Your Wallet</p>
        <h1 className="font-serif text-3xl font-bold text-bronze mt-1">Balance</h1>
        <div className="w-20 h-1 bg-gradient-to-r from-copper via-penny to-sage rounded-full my-2.5 opacity-90" />
      </div>

      <div className="bg-card rounded-3xl border border-border p-8 shadow-lg mt-2 relative overflow-hidden text-center flex flex-col items-center">
        <div className="absolute -top-10 -right-10 w-44 h-44 bg-copper/10 rounded-full blur-2xl" />
        <p className="font-serif text-6xl font-bold text-bronze tabular-nums" data-testid="wallet-balance">${data.balance.toFixed(2)}</p>
        <div className="grid grid-cols-2 gap-3 mt-6 w-full">
          <div className="bg-secondary rounded-2xl p-3">
            <p className="text-xs text-muted-foreground">From direct tips</p>
            <p className="font-serif text-xl font-semibold text-bronze">${data.tip_total.toFixed(2)}</p>
          </div>
          <div className="bg-secondary rounded-2xl p-3">
            <p className="text-xs text-muted-foreground">From community jar</p>
            <p className="font-serif text-xl font-semibold text-bronze">${data.split_total.toFixed(2)}</p>
          </div>
        </div>
        <Button data-testid="cashout-btn" onClick={() => setShowCashout(true)} disabled={data.balance <= 0}
          className="w-full bg-copper hover:bg-copper/90 text-white rounded-full py-6 mt-6">
          <ArrowDownToLine className="w-4 h-4 mr-2" /> Cash out by email transfer
        </Button>
      </div>

      <Dialog open={showCashout} onOpenChange={setShowCashout}>
        <DialogContent data-testid="cashout-dialog" className="sm:max-w-md rounded-3xl">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-bronze flex items-center gap-2"><Mail className="w-5 h-5 text-copper" /> Email transfer</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground -mt-1">We'll send your ${data.balance.toFixed(2)} balance to this email.</p>
          <div><Label>Email to receive funds</Label>
            <Input data-testid="cashout-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className="rounded-xl mt-1" /></div>
          <Button data-testid="cashout-confirm" onClick={cashout} disabled={cashing} className="w-full bg-copper hover:bg-copper/90 text-white rounded-full py-6">
            {cashing ? "Sending…" : `Send $${data.balance.toFixed(2)}`}
          </Button>
        </DialogContent>
      </Dialog>

      <h2 className="font-serif text-xl font-semibold text-bronze mt-8 mb-3 text-center">Activity</h2>
      <div className="space-y-2" data-testid="wallet-ledger">
        {data.ledger.length === 0 ? (
          <p className="text-muted-foreground text-center py-10">No activity yet. Tips and daily jar shares will appear here.</p>
        ) : data.ledger.map((l) => {
          const Ic = icon[l.type] || Coins;
          return (
            <div key={l.ledger_id} className="flex items-center gap-3 bg-card rounded-2xl border border-border p-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${l.amount < 0 ? "bg-bronze/15 text-bronze" : "bg-sage/15 text-sage"}`}><Ic className="w-5 h-5" /></div>
              <div className="flex-1 min-w-0">
                <p className="font-medium">{labelType[l.type] || l.type}</p>
                <p className="text-xs text-muted-foreground truncate">{l.note}</p>
              </div>
              <p className={`font-mono font-bold ${l.amount < 0 ? "text-bronze" : "text-sage"}`}>
                {l.amount < 0 ? "-" : "+"}${Math.abs(l.amount).toFixed(2)}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}
